import styled from '@emotion/styled';
import { Drawer, DrawerHeader } from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import { normalizeImgSource } from '../../..';
import { AddClassroomModal } from '../../../classrooms-management/components/add-classroom-modal';
import { ClassroomConfigSection } from './components/config-section';
import { KnowMoreSection } from './components/section';
import { useKnowMoreDrawer } from './hooks/use-know-more-drawer';
import imageAtlas1 from './img/atlas_01.png';
import imageAtlas2 from './img/atlas_02.png';
import imageAtlas3 from './img/atlas_03.png';
import imageAtlas4 from './img/atlas_04.png';

const Wrapper = styled.div`
  width: 584px;
  padding: 16px 32px 32px 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

type KnowMoreDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

function KnowMoreDrawer({ isOpen, onClose }: KnowMoreDrawerProps): JSX.Element {
  const { t } = useTranslation();
  const {
    isModifyModalOpen,
    classroom,
    handleOpenModifyModal,
    handleCloseModifyModal,
    handleModalSuccess,
  } = useKnowMoreDrawer();

  return (
    <>
      <Drawer
        key="know-more-about-atlas-drawer"
        open={isOpen}
        roundedBorder
        onClose={onClose}
        customStyles={{ width: '584px' }}
        closeOnClickOutside
      >
        <Wrapper>
          <DrawerHeader
            title={t('reports.personalizedPractice.knowMore.subtitle1')}
            onCloseDrawer={onClose}
          />
          <ContentWrapper>
            <KnowMoreSection
              bodyTranslationKey="reports.personalizedPractice.knowMore.text1"
              image={{
                src: normalizeImgSource(imageAtlas1),
                alt: 'Training zone',
                position: 'left',
              }}
            />
            <SectionGroup>
              <KnowMoreSection
                titleTranslationKey="reports.personalizedPractice.knowMore.header1"
                bodyTranslationKey="reports.personalizedPractice.knowMore.text2"
                image={{
                  src: normalizeImgSource(imageAtlas2),
                  alt: 'Individual practice',
                  position: 'right',
                }}
              />
              <KnowMoreSection bodyTranslationKey="reports.personalizedPractice.knowMore.text3" />
            </SectionGroup>
            <SectionGroup>
              <KnowMoreSection
                titleTranslationKey="reports.personalizedPractice.knowMore.header2"
                bodyTranslationKey="reports.personalizedPractice.knowMore.text4"
                image={{
                  src: normalizeImgSource(imageAtlas3),
                  alt: 'Training zone',
                  position: 'left',
                }}
              />
              <KnowMoreSection bodyTranslationKey="reports.personalizedPractice.knowMore.text5" />
            </SectionGroup>
            <KnowMoreSection
              titleTranslationKey="reports.personalizedPractice.knowMore.header3"
              bodyTranslationKey="reports.personalizedPractice.knowMore.text6"
              image={{
                src: normalizeImgSource(imageAtlas4),
                alt: 'Individual practice',
                position: 'right',
              }}
            />
            <Divider />
            <KnowMoreSection
              titleTranslationKey="reports.personalizedPractice.knowMore.header4"
              bodyTranslationKey="reports.personalizedPractice.knowMore.text7"
            />
            <ClassroomConfigSection
              titleTranslationKey="reports.personalizedPractice.knowMore.header5"
              classroom={classroom || undefined}
              onClickModifyButton={handleOpenModifyModal}
            />
          </ContentWrapper>
        </Wrapper>
      </Drawer>
      <AddClassroomModal
        key={`modal-${classroom?.id}`}
        classroom={classroom || undefined}
        show={isModifyModalOpen}
        onClose={handleCloseModifyModal}
        onSuccess={handleModalSuccess}
        zIndex={35}
      />
    </>
  );
}

export { KnowMoreDrawer };
